import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { 
  Users,
  Building2,
  Package,
  FileText,
  Newspaper,
  Mail,
  TrendingUp,
  CheckCircle2,
  XCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const COLORS = ['#0D4F8B', '#10B981', '#D4AF37', '#64748B', '#EF4444'];

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [supplierTotal, setSupplierTotal] = useState(0);
  const [productTotal, setProductTotal] = useState(0);
  const [rfqTotal, setRfqTotal] = useState(0);
  const [contactTotal, setContactTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userStatusFilter, setUserStatusFilter] = useState("all");
  const [userPage, setUserPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkUserRole, setBulkUserRole] = useState("buyer");

  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierStatusFilter, setSupplierStatusFilter] = useState("all");
  const [supplierPage, setSupplierPage] = useState(1);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  const [productSearch, setProductSearch] = useState("");
  const [productCategoryFilter, setProductCategoryFilter] = useState("all");
  const [productPage, setProductPage] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [rfqSearch, setRfqSearch] = useState("");
  const [rfqStatusFilter, setRfqStatusFilter] = useState("all");
  const [rfqPage, setRfqPage] = useState(1);

  const [contactSearch, setContactSearch] = useState("");
  const [contactPage, setContactPage] = useState(1);

  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [editProductForm, setEditProductForm] = useState({
    name: "",
    category: "",
    price_min_usd: "",
    price_max_usd: "",
    moq: "",
    lead_time_days: ""
  });
  const [editProductImage, setEditProductImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editClearImage, setEditClearImage] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchCategories();
  }, []);

  useEffect(() => {
    setUserPage(1);
  }, [userSearch, userRoleFilter, userStatusFilter]);

  useEffect(() => {
    setSupplierPage(1);
  }, [supplierSearch, supplierStatusFilter]);

  useEffect(() => {
    setProductPage(1);
  }, [productSearch, productCategoryFilter]);

  useEffect(() => {
    setRfqPage(1);
  }, [rfqSearch, rfqStatusFilter]);

  useEffect(() => {
    setContactPage(1);
  }, [contactSearch]);

  useEffect(() => {
    fetchUsers();
  }, [userSearch, userRoleFilter, userStatusFilter, userPage]);

  useEffect(() => {
    fetchSuppliers();
  }, [supplierSearch, supplierStatusFilter, supplierPage]);

  useEffect(() => {
    fetchProducts();
  }, [productSearch, productCategoryFilter, productPage]);

  useEffect(() => {
    fetchRfqs();
  }, [rfqSearch, rfqStatusFilter, rfqPage]);

  useEffect(() => {
    fetchContacts();
  }, [contactSearch, contactPage]);

  const PAGE_SIZE = 10;

  const getImageUrl = (paths) => {
    if (!paths || paths.length === 0) return null;
    const path = paths[0];
    if (!path) return null;
    return path.startsWith("http") ? path : `${API_URL}${path}`;
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const analyticsRes = await axios.get(`${API_URL}/api/analytics/dashboard`);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load dashboard data');
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        params: {
          search: userSearch || undefined,
          role: userRoleFilter,
          status_filter: userStatusFilter,
          page: userPage,
          page_size: PAGE_SIZE
        }
      });
      setUsers(response.data.items || []);
      setUserTotal(response.data.total || 0);
    } catch (err) {
      toast.error('Failed to load users');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/suppliers`, {
        params: {
          search: supplierSearch || undefined,
          status_filter: supplierStatusFilter,
          page: supplierPage,
          page_size: PAGE_SIZE
        }
      });
      setSuppliers(response.data.items || []);
      setSupplierTotal(response.data.total || 0);
    } catch (err) {
      toast.error('Failed to load suppliers');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/products`, {
        params: {
          search: productSearch || undefined,
          category: productCategoryFilter,
          page: productPage,
          page_size: PAGE_SIZE
        }
      });
      setProducts(response.data.items || []);
      setProductTotal(response.data.total || 0);
    } catch (err) {
      toast.error('Failed to load products');
    }
  };

  const fetchRfqs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/rfqs`, {
        params: {
          search: rfqSearch || undefined,
          status_filter: rfqStatusFilter,
          page: rfqPage,
          page_size: PAGE_SIZE
        }
      });
      setRfqs(response.data.items || []);
      setRfqTotal(response.data.total || 0);
    } catch (err) {
      toast.error('Failed to load RFQs');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/contacts`, {
        params: {
          search: contactSearch || undefined,
          page: contactPage,
          page_size: PAGE_SIZE
        }
      });
      setContacts(response.data.items || []);
      setContactTotal(response.data.total || 0);
    } catch (err) {
      toast.error('Failed to load contacts');
    }
  };

  const handleVerifySupplier = async (supplierId, verified) => {
    try {
      await axios.put(`${API_URL}/api/suppliers/${supplierId}/verify?verified=${verified}`);
      toast.success(verified ? 'Supplier verified' : 'Verification removed');
      fetchSuppliers();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to update supplier');
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    if (user && userId === user.id && role !== "admin") {
      toast.error("You cannot change your own admin role.");
      return;
    }
    try {
      await axios.put(`${API_URL}/api/users/${userId}/role?role=${role}`);
      toast.success('User role updated');
      fetchUsers();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const handleUpdateUserStatus = async (userId, active) => {
    if (user && userId === user.id && !active) {
      toast.error("You cannot suspend your own account.");
      return;
    }
    try {
      await axios.put(`${API_URL}/api/users/${userId}/status?active=${active}`);
      toast.success(active ? 'User activated' : 'User suspended');
      fetchUsers();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const toggleSelection = (id, selected, setSelected) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((item) => item !== id));
    } else {
      setSelected([...selected, id]);
    }
  };

  const bulkUpdateUserStatus = async (active) => {
    if (selectedUsers.length === 0) {
      toast.error('Select at least one user');
      return;
    }
    const targetUsers = user ? selectedUsers.filter((id) => id !== user.id || active) : selectedUsers;
    if (targetUsers.length === 0) {
      toast.error("You cannot suspend your own account.");
      return;
    }
    try {
      await Promise.all(
        targetUsers.map((userId) =>
          axios.put(`${API_URL}/api/users/${userId}/status?active=${active}`)
        )
      );
      toast.success(active ? 'Users activated' : 'Users suspended');
      setSelectedUsers([]);
      fetchUsers();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to update users');
    }
  };

  const bulkUpdateUserRole = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Select at least one user');
      return;
    }
    const targetUsers = user && bulkUserRole !== "admin"
      ? selectedUsers.filter((id) => id !== user.id)
      : selectedUsers;
    if (targetUsers.length === 0) {
      toast.error("You cannot change your own admin role.");
      return;
    }
    try {
      await Promise.all(
        targetUsers.map((userId) =>
          axios.put(`${API_URL}/api/users/${userId}/role?role=${bulkUserRole}`)
        )
      );
      toast.success('User roles updated');
      setSelectedUsers([]);
      fetchUsers();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to update roles');
    }
  };

  const bulkVerifySuppliers = async (verified) => {
    if (selectedSuppliers.length === 0) {
      toast.error('Select at least one supplier');
      return;
    }
    try {
      await Promise.all(
        selectedSuppliers.map((supplierId) =>
          axios.put(`${API_URL}/api/suppliers/${supplierId}/verify?verified=${verified}`)
        )
      );
      toast.success(verified ? 'Suppliers verified' : 'Suppliers unverified');
      setSelectedSuppliers([]);
      fetchSuppliers();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to update suppliers');
    }
  };

  const handleDeleteProduct = async (productId) => {
    const confirmed = window.confirm('Delete this product? This cannot be undone.');
    if (!confirmed) return;
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  const bulkDeleteProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Select at least one product');
      return;
    }
    const confirmed = window.confirm('Delete selected products? This cannot be undone.');
    if (!confirmed) return;
    try {
      const results = await Promise.allSettled(
        selectedProducts.map((productId) =>
          axios.delete(`${API_URL}/api/products/${productId}`)
        )
      );
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length > 0) {
        toast.error(`Failed to delete ${failed.length} products`);
      } else {
        toast.success('Products deleted');
      }
      setSelectedProducts([]);
      fetchProducts();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to delete products');
    }
  };

  const openEditProductModal = (product) => {
    setEditProduct(product);
    setEditProductForm({
      name: product.name || "",
      category: product.category || "",
      price_min_usd: product.price_min_usd?.toString() || "",
      price_max_usd: product.price_max_usd?.toString() || "",
      moq: product.moq?.toString() || "",
      lead_time_days: product.lead_time_days?.toString() || ""
    });
    setEditProductImage(null);
    setEditImagePreview(getImageUrl(product.image_paths));
    setEditClearImage(false);
    setShowEditProductModal(true);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Please select a JPG, PNG, or WEBP image.");
      return;
    }
    setEditProductImage(file);
    setEditClearImage(false);
    if (editImagePreview && editImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImagePreview(URL.createObjectURL(file));
  };

  const clearEditImage = () => {
    if (editImagePreview && editImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditProductImage(null);
    setEditImagePreview(null);
    setEditClearImage(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editProduct) return;
    const minPrice = parseFloat(editProductForm.price_min_usd);
    const maxPrice = parseFloat(editProductForm.price_max_usd);
    const moq = parseInt(editProductForm.moq, 10);
    const leadTime = parseInt(editProductForm.lead_time_days, 10);
    if (
      !editProductForm.name ||
      !editProductForm.category ||
      Number.isNaN(minPrice) ||
      Number.isNaN(maxPrice) ||
      Number.isNaN(moq) ||
      Number.isNaN(leadTime)
    ) {
      toast.error("Please fill all fields with valid values.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("name", editProductForm.name);
      formData.append("category", editProductForm.category);
      formData.append("price_min_usd", minPrice.toString());
      formData.append("price_max_usd", maxPrice.toString());
      formData.append("moq", moq.toString());
      formData.append("lead_time_days", leadTime.toString());
      if (editClearImage) {
        formData.append("clear_image", "true");
      }
      if (editProductImage) {
        formData.append("image", editProductImage);
      }
      await axios.put(`${API_URL}/api/products/${editProduct.id}/form`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success('Product updated');
      setShowEditProductModal(false);
      setEditProduct(null);
      setEditProductImage(null);
      setEditImagePreview(null);
      setEditClearImage(false);
      fetchProducts();
    } catch (err) {
      toast.error('Failed to update product');
    }
  };

  const handleUpdateRfqStatus = async (rfqId, status) => {
    try {
      await axios.put(`${API_URL}/api/rfqs/${rfqId}/status?status=${status}`);
      toast.success('RFQ status updated');
      fetchRfqs();
      fetchAnalytics();
    } catch (err) {
      toast.error('Failed to update RFQ');
    }
  };

  const isUserActive = (userItem) => userItem?.is_active !== false;

  const userTotalPages = Math.max(Math.ceil(userTotal / PAGE_SIZE), 1);
  const supplierTotalPages = Math.max(Math.ceil(supplierTotal / PAGE_SIZE), 1);
  const productTotalPages = Math.max(Math.ceil(productTotal / PAGE_SIZE), 1);
  const rfqTotalPages = Math.max(Math.ceil(rfqTotal / PAGE_SIZE), 1);
  const contactTotalPages = Math.max(Math.ceil(contactTotal / PAGE_SIZE), 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D4F8B]"></div>
      </div>
    );
  }

  const statsCards = [
    { title: 'Total Users', value: analytics?.totals?.users || 0, icon: Users, color: 'text-[#0D4F8B]' },
    { title: 'Active Users', value: analytics?.totals?.active_users || 0, icon: Users, color: 'text-[#10B981]' },
    { title: 'Suspended Users', value: analytics?.totals?.suspended_users || 0, icon: Users, color: 'text-red-500' },
    { title: 'Suppliers', value: analytics?.totals?.suppliers || 0, icon: Building2, color: 'text-[#10B981]' },
    { title: 'Verified Suppliers', value: analytics?.totals?.verified_suppliers || 0, icon: CheckCircle2, color: 'text-[#0D4F8B]' },
    { title: 'Products', value: analytics?.totals?.products || 0, icon: Package, color: 'text-[#D4AF37]' },
    { title: 'RFQs', value: analytics?.totals?.rfqs || 0, icon: FileText, color: 'text-purple-500' },
  ];

  const usersByRoleData = [
    { name: 'Buyers', value: analytics?.users_by_role?.buyers || 0 },
    { name: 'Suppliers', value: analytics?.users_by_role?.suppliers || 0 },
  ];

  const rfqsByStatusData = [
    { name: 'Open', value: analytics?.rfqs_by_status?.open || 0 },
    { name: 'Awarded', value: analytics?.rfqs_by_status?.awarded || 0 },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-main py-8">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('admin.title')}
          </h1>
          <p className="text-slate-500">Manage your marketplace</p>
        </div>
      </div>

      <div className="container-main py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          {statsCards.map((stat, index) => (
            <Card key={index} className="card-base">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#0D4F8B]" />
                Users by Role
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={usersByRoleData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {usersByRoleData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0D4F8B]" />
                Top Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={analytics?.top_categories || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0D4F8B" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList>
            <TabsTrigger value="users">{t('admin.users')}</TabsTrigger>
            <TabsTrigger value="suppliers">{t('admin.suppliers')}</TabsTrigger>
            <TabsTrigger value="products">{t('admin.products')}</TabsTrigger>
            <TabsTrigger value="rfqs">{t('admin.rfqs')}</TabsTrigger>
            <TabsTrigger value="contacts">{t('admin.contacts')}</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="card-base">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        placeholder="Search users..."
                        aria-label="Search users"
                        className="w-full sm:w-64"
                      />
                      <select
                        value={userRoleFilter}
                        onChange={(e) => setUserRoleFilter(e.target.value)}
                        aria-label="Filter users by role"
                        className="text-sm border rounded px-2 py-2"
                      >
                        <option value="all">All roles</option>
                        <option value="buyer">Buyer</option>
                        <option value="supplier">Supplier</option>
                        <option value="admin">Admin</option>
                      </select>
                      <select
                        value={userStatusFilter}
                        onChange={(e) => setUserStatusFilter(e.target.value)}
                        aria-label="Filter users by status"
                        className="text-sm border rounded px-2 py-2"
                      >
                        <option value="all">All statuses</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-slate-500">
                        Total: {userTotal} • Selected: {selectedUsers.length}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => bulkUpdateUserStatus(true)}>
                        Activate Selected
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => bulkUpdateUserStatus(false)}>
                        Suspend Selected
                      </Button>
                      <select
                        value={bulkUserRole}
                        onChange={(e) => setBulkUserRole(e.target.value)}
                        aria-label="Bulk update role"
                        className="text-sm border rounded px-2 py-2"
                      >
                        <option value="buyer">Set role: Buyer</option>
                        <option value="supplier">Set role: Supplier</option>
                        <option value="admin">Set role: Admin</option>
                      </select>
                      <Button size="sm" className="bg-[#0D4F8B]" onClick={bulkUpdateUserRole}>
                        Apply Role
                      </Button>
                    </div>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          checked={users.length > 0 && users.every((u) => selectedUsers.includes(u.id))}
                          aria-label="Select all users on this page"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([
                                ...new Set([...selectedUsers, ...users.map((u) => u.id)])
                              ]);
                            } else {
                              setSelectedUsers(selectedUsers.filter((id) => !users.some((u) => u.id === id)));
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((u) => (
                        <TableRow key={u.id} className={selectedUsers.includes(u.id) ? "bg-slate-50" : ""}>
                          <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(u.id)}
                            aria-label={`Select user ${u.name}`}
                            onChange={() => toggleSelection(u.id, selectedUsers, setSelectedUsers)}
                          />
                          </TableCell>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{u.role}</Badge>
                          </TableCell>
                          <TableCell>
                            {isUserActive(u) ? (
                            <Badge className="bg-[#10B981]" aria-label="Active user">Active</Badge>
                          ) : (
                            <Badge variant="outline" aria-label="Suspended user">Suspended</Badge>
                          )}
                          </TableCell>
                          <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                            <select
                              value={u.role}
                              onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                              aria-label={`Change role for ${u.name}`}
                              className="text-sm border rounded px-2 py-1"
                            >
                                <option value="buyer">Buyer</option>
                                <option value="supplier">Supplier</option>
                                <option value="admin">Admin</option>
                              </select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateUserStatus(u.id, !isUserActive(u))}
                              aria-label={`${isUserActive(u) ? 'Suspend' : 'Activate'} user ${u.name}`}
                            >
                              {isUserActive(u) ? 'Suspend' : 'Activate'}
                            </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between p-4 border-t border-slate-100 text-sm text-slate-500">
                  <span>
                    Showing {users.length} of {userTotal} users
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUserPage(Math.max(userPage - 1, 1))}
                      disabled={userPage === 1}
                    >
                      Prev
                    </Button>
                    <span>
                      Page {userPage} of {userTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUserPage(Math.min(userPage + 1, userTotalPages))}
                      disabled={userPage === userTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Suppliers Tab */}
          <TabsContent value="suppliers">
            <Card className="card-base">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        value={supplierSearch}
                        onChange={(e) => setSupplierSearch(e.target.value)}
                        placeholder="Search suppliers..."
                        aria-label="Search suppliers"
                        className="w-full sm:w-64"
                      />
                      <select
                        value={supplierStatusFilter}
                        onChange={(e) => setSupplierStatusFilter(e.target.value)}
                        aria-label="Filter suppliers by verification status"
                        className="text-sm border rounded px-2 py-2"
                      >
                        <option value="all">All statuses</option>
                        <option value="verified">Verified</option>
                        <option value="unverified">Unverified</option>
                      </select>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-slate-500">
                        Total: {supplierTotal} • Selected: {selectedSuppliers.length}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => bulkVerifySuppliers(true)}>
                        Verify Selected
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => bulkVerifySuppliers(false)}>
                        Unverify Selected
                      </Button>
                    </div>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          checked={suppliers.length > 0 && suppliers.every((s) => selectedSuppliers.includes(s.id))}
                          aria-label="Select all suppliers on this page"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSuppliers([
                                ...new Set([...selectedSuppliers, ...suppliers.map((s) => s.id)])
                              ]);
                            } else {
                              setSelectedSuppliers(selectedSuppliers.filter((id) => !suppliers.some((s) => s.id === id)));
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500">
                          No suppliers found
                        </TableCell>
                      </TableRow>
                    ) : (
                      suppliers.map((s) => (
                        <TableRow key={s.id} className={selectedSuppliers.includes(s.id) ? "bg-slate-50" : ""}>
                          <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedSuppliers.includes(s.id)}
                            aria-label={`Select supplier ${s.company_name}`}
                            onChange={() => toggleSelection(s.id, selectedSuppliers, setSelectedSuppliers)}
                          />
                          </TableCell>
                          <TableCell className="font-medium">{s.company_name}</TableCell>
                          <TableCell>{s.location}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {s.categories.slice(0, 2).map((cat) => (
                                <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {s.verified ? (
                              <Badge className="bg-[#10B981]" aria-label="Verified supplier">Verified</Badge>
                            ) : (
                              <Badge variant="outline" aria-label="Unverified supplier">Pending</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {s.verified ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleVerifySupplier(s.id, false)}
                                aria-label={`Unverify supplier ${s.company_name}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Unverify
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className="bg-[#10B981] hover:bg-[#059669]"
                                onClick={() => handleVerifySupplier(s.id, true)}
                                aria-label={`Verify supplier ${s.company_name}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Verify
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between p-4 border-t border-slate-100 text-sm text-slate-500">
                  <span>
                    Showing {suppliers.length} of {supplierTotal} suppliers
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSupplierPage(Math.max(supplierPage - 1, 1))}
                      disabled={supplierPage === 1}
                    >
                      Prev
                    </Button>
                    <span>
                      Page {supplierPage} of {supplierTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSupplierPage(Math.min(supplierPage + 1, supplierTotalPages))}
                      disabled={supplierPage === supplierTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card className="card-base">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="Search products..."
                        aria-label="Search products"
                        className="w-full sm:w-64"
                      />
                      <select
                        value={productCategoryFilter}
                        onChange={(e) => setProductCategoryFilter(e.target.value)}
                        aria-label="Filter products by category"
                        className="text-sm border rounded px-2 py-2"
                      >
                        <option value="all">All categories</option>
                        {categories.map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-slate-500">
                        Total: {productTotal} • Selected: {selectedProducts.length}
                      </span>
                      <Button variant="outline" size="sm" onClick={bulkDeleteProducts}>
                        Delete Selected
                      </Button>
                    </div>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          checked={products.length > 0 && products.every((p) => selectedProducts.includes(p.id))}
                          aria-label="Select all products on this page"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProducts([
                                ...new Set([...selectedProducts, ...products.map((p) => p.id)])
                              ]);
                            } else {
                              setSelectedProducts(selectedProducts.filter((id) => !products.some((p) => p.id === id)));
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Image</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price (USD)</TableHead>
                      <TableHead>MOQ (Minimum Order Quantity)</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500">
                          No products found
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((p) => (
                        <TableRow key={p.id} className={selectedProducts.includes(p.id) ? "bg-slate-50" : ""}>
                          <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(p.id)}
                            aria-label={`Select product ${p.name}`}
                            onChange={() => toggleSelection(p.id, selectedProducts, setSelectedProducts)}
                          />
                          </TableCell>
                          <TableCell>
                            {getImageUrl(p.image_paths) ? (
                            <img
                              src={getImageUrl(p.image_paths)}
                              alt={p.name}
                              className="h-10 w-10 rounded-md object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-md bg-slate-100" aria-hidden="true" />
                          )}
                          </TableCell>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{p.category}</Badge>
                          </TableCell>
                          <TableCell>${p.price_min_usd} - ${p.price_max_usd}</TableCell>
                          <TableCell>{p.moq}</TableCell>
                          <TableCell>{p.supplier_name || '-'}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditProductModal(p)}
                              aria-label={`Edit product ${p.name}`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteProduct(p.id)}
                              aria-label={`Delete product ${p.name}`}
                            >
                              Delete
                            </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between p-4 border-t border-slate-100 text-sm text-slate-500">
                  <span>
                    Showing {products.length} of {productTotal} products
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setProductPage(Math.max(productPage - 1, 1))}
                      disabled={productPage === 1}
                    >
                      Prev
                    </Button>
                    <span>
                      Page {productPage} of {productTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setProductPage(Math.min(productPage + 1, productTotalPages))}
                      disabled={productPage === productTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Dialog open={showEditProductModal} onOpenChange={setShowEditProductModal}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Product</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateProduct} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Product Name *</label>
                    <Input
                      value={editProductForm.name}
                      onChange={(e) => setEditProductForm({ ...editProductForm, name: e.target.value })}
                      placeholder="Product name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Category *</label>
                    <Input
                      value={editProductForm.category}
                      onChange={(e) => setEditProductForm({ ...editProductForm, category: e.target.value })}
                      placeholder="Category"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Min Price (USD) *</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editProductForm.price_min_usd}
                        onChange={(e) => setEditProductForm({ ...editProductForm, price_min_usd: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Max Price (USD) *</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editProductForm.price_max_usd}
                        onChange={(e) => setEditProductForm({ ...editProductForm, price_max_usd: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">MOQ *</label>
                      <Input
                        type="number"
                        value={editProductForm.moq}
                        onChange={(e) => setEditProductForm({ ...editProductForm, moq: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Lead Time (days) *</label>
                      <Input
                        type="number"
                        value={editProductForm.lead_time_days}
                        onChange={(e) => setEditProductForm({ ...editProductForm, lead_time_days: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Product Image</label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleEditImageChange}
                      className="h-11 w-full cursor-pointer rounded-md border border-slate-200 bg-white text-sm text-slate-600 file:mr-3 file:h-9 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
                    />
                    {editImagePreview ? (
                      <div className="mt-3 space-y-2">
                        <img
                          src={editImagePreview}
                          alt={editProductForm.name || "Preview"}
                          className="h-40 w-full rounded-lg object-contain border"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={clearEditImage}
                          className="h-10"
                        >
                          Clear Image
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">No image selected.</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full bg-[#10B981] hover:bg-[#059669]">
                    Save Changes
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* RFQs Tab */}
          <TabsContent value="rfqs">
            <Card className="card-base">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <Input
                      value={rfqSearch}
                      onChange={(e) => setRfqSearch(e.target.value)}
                      placeholder="Search RFQs..."
                      aria-label="Search RFQs"
                      className="w-full sm:w-64"
                    />
                    <select
                      value={rfqStatusFilter}
                      onChange={(e) => setRfqStatusFilter(e.target.value)}
                      aria-label="Filter RFQs by status"
                      className="text-sm border rounded px-2 py-2"
                    >
                      <option value="all">All statuses</option>
                      <option value="open">Open</option>
                      <option value="closed">Closed</option>
                      <option value="awarded">Awarded</option>
                    </select>
                    <span className="text-xs text-slate-500">
                      Total: {rfqTotal}
                    </span>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Buyer</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Specifications</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rfqs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500">
                          No RFQs found
                        </TableCell>
                      </TableRow>
                    ) : (
                      rfqs.map((rfq) => (
                        <TableRow key={rfq.id}>
                          <TableCell className="font-medium">{rfq.buyer_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{rfq.category}</Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{rfq.specs}</TableCell>
                          <TableCell>{rfq.quantity}</TableCell>
                          <TableCell>{rfq.destination}</TableCell>
                          <TableCell>
                          <Badge className={
                            rfq.status === 'open' ? 'bg-[#10B981]' :
                            rfq.status === 'awarded' ? 'bg-[#D4AF37]' : 'bg-slate-500'
                          } aria-label={`RFQ status ${rfq.status}`}>
                            {rfq.status}
                          </Badge>
                          </TableCell>
                        <TableCell>
                          <select
                            value={rfq.status}
                            onChange={(e) => {
                              const nextStatus = e.target.value;
                              const confirmed = window.confirm(`Change status to ${nextStatus}?`);
                              if (confirmed) {
                                handleUpdateRfqStatus(rfq.id, nextStatus);
                              }
                            }}
                            aria-label={`Change RFQ status for ${rfq.category}`}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                            <option value="awarded">Awarded</option>
                            </select>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between p-4 border-t border-slate-100 text-sm text-slate-500">
                  <span>
                    Showing {rfqs.length} of {rfqTotal} RFQs
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRfqPage(Math.max(rfqPage - 1, 1))}
                      disabled={rfqPage === 1}
                    >
                      Prev
                    </Button>
                    <span>
                      Page {rfqPage} of {rfqTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRfqPage(Math.min(rfqPage + 1, rfqTotalPages))}
                      disabled={rfqPage === rfqTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card className="card-base">
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <Input
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Search contacts..."
                      aria-label="Search contacts"
                      className="w-full sm:w-64"
                    />
                    <span className="text-xs text-slate-500">
                      Total: {contactTotal}
                    </span>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-slate-500">
                          No contacts found
                        </TableCell>
                      </TableRow>
                    ) : (
                      contacts.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell>{c.email}</TableCell>
                          <TableCell className="max-w-xs truncate">{c.message}</TableCell>
                          <TableCell>{new Date(c.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-between p-4 border-t border-slate-100 text-sm text-slate-500">
                  <span>
                    Showing {contacts.length} of {contactTotal} contacts
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setContactPage(Math.max(contactPage - 1, 1))}
                      disabled={contactPage === 1}
                    >
                      Prev
                    </Button>
                    <span>
                      Page {contactPage} of {contactTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setContactPage(Math.min(contactPage + 1, contactTotalPages))}
                      disabled={contactPage === contactTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
